import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

import { AdminLayout } from "@/components/admin/layout/AdminLayout";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { PostForm } from "@/components/admin/blog/PostForm";
import { blogAdminService, BlogPostFormData } from "@/services/blogAdminService";
import type { BlogPost } from "@/types/blog";
import { Button } from "@/components/ui/button";

export default function EditPost() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin, loading: authLoading } = useAdminAuth();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate("/admin-login");
    }
  }, [authLoading, isAdmin, navigate]);

  useEffect(() => {
    if (id) {
      loadPost();
    }
  }, [id]);

  async function loadPost() {
    if (!id) return;

    try {
      setLoading(true);
      const data = await blogAdminService.getPost(id);
      setPost(data);
    } catch (error: any) {
      toast.error(error.message || "Erro ao carregar post");
      navigate("/admin/posts");
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(data: BlogPostFormData, published: boolean) {
    if (!id) return;

    try {
      await blogAdminService.updatePost(id, { ...data, published });
      toast.success("Post atualizado com sucesso!");
      navigate("/admin/posts");
    } catch (error: any) {
      toast.error(error.message || "Erro ao atualizar post");
      throw error;
    }
  }

  async function handleDelete() {
    if (!id || !confirm("Tem certeza que deseja excluir este post?")) return;

    try {
      await blogAdminService.deletePost(id);
      toast.success("Post excluído com sucesso!");
      navigate("/admin/posts");
    } catch (error: any) {
      toast.error(error.message || "Erro ao excluir post");
    }
  }

  if (authLoading || !isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <AdminLayout
        title="Editar conteúdo"
        description="Atualize títulos, imagens e metadados com segurança antes de publicar."
        breadcrumbs={[
          { label: "Admin", href: "/admin" },
          { label: "Blog", href: "/admin/posts" },
          { label: "Carregando" },
        ]}
      >
        <section className="flex min-h-[40vh] items-center justify-center text-sm text-slate-600">
          Carregando informações do post...
        </section>
      </AdminLayout>
    );
  }

  if (!post) {
    return (
      <AdminLayout
        title="Conteúdo não encontrado"
        description="O post solicitado pode ter sido removido ou não existe mais."
        breadcrumbs={[
          { label: "Admin", href: "/admin" },
          { label: "Blog", href: "/admin/posts" },
          { label: "Erro" },
        ]}
        actions={
          <Button variant="outline" asChild>
            <Link to="/admin/posts">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar para lista
            </Link>
          </Button>
        }
      >
        <section className="flex min-h-[40vh] items-center justify-center text-sm text-slate-600">
          Post não encontrado.
        </section>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Editar conteúdo"
      description="Atualize títulos, imagens e metadados com segurança antes de publicar."
      breadcrumbs={[
        { label: "Admin", href: "/admin" },
        { label: "Blog", href: "/admin/posts" },
        { label: post.title || "Editar" },
      ]}
      actions={
        <Button variant="outline" asChild>
          <Link to="/admin/posts">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar para lista
          </Link>
        </Button>
      }
    >
      <section className="mx-auto max-w-5xl">
        <PostForm
          post={post}
          onSubmit={handleSubmit}
          onDelete={handleDelete}
        />
      </section>
    </AdminLayout>
  );
}
